import kebabCase from 'lodash/kebabCase'
import _truncate from 'lodash/truncate' // eslint-disable-line no-unused-vars
import type { RouteLocationNormalizedGeneric as Route } from 'vue-router'

export function stripTags(string) {
  // note: when we move to @zicht/nuxt-util, we will need to add support for non-strings for safety
  return (string || '').replace(/(<([^>]+)>)/gi, '')
}

function tcArgs(args) {
  if (args && Array.isArray(args) && args.length > 0 && typeof args[0] === 'number') {
    args[0] = { n: args[0] }
  }
  return args
}

export function getTranslationPrefixer(i18n: ReturnType<typeof useI18n>) {
  return (componentName: string, groupName: string | undefined = 'default') => {
    const componentName_ = kebabCase(componentName)
    const groupName_ = groupName ? kebabCase(groupName) : undefined
    const prefix_ = groupName_ ? `${componentName_}.${groupName_}` : componentName_

    return {
      translationComponent_: componentName_,
      translationGroup_: groupName_,
      translationPrefix_: prefix_,

      // Helper functions that prefix and normalize the translation key
      k_: (key: string) => key && `${prefix_}.${kebabCase(key)}`,
      t_: (key: string, ...args) => key && i18n.t(`${prefix_}.${kebabCase(key)}`, ...args),
      tc_: (key: string, ...args) => key && i18n.t(`${prefix_}.${kebabCase(key)}`, ...tcArgs(args)),
      te_: (key: string, ...args) => key && i18n.te(`${prefix_}.${kebabCase(key)}`, ...args),

      // Helper function that automatically converts strings into Date objects
      d_: (value, pattern) => {
        if (!value) {
          return ''
        }
        const date = typeof value === 'object' ? value : new Date(value)
        return pattern === 'iso' ? date.toISOString() : i18n?.d(date, pattern)
      },
    }
  }
}

export function setupTranslationPrefixer(componentName, groupName = 'default') {
  const i18n = useI18n()
  return getTranslationPrefixer(i18n)(componentName, groupName)
}

/**
 * Truncate a string to `options.length` characters, separated by word boundaries
 *
 * @see https://lodash.com/docs/4.17.15#truncate
 */
export function truncate(string, options) {
  // Separate on word boundaries
  const separator = options?.separator || /[.,!? ]/
  // Use the ellipsis character instead of three dots
  const omission = options?.omission || '&hellip;'
  // Add 6 to the length because '&hellip;' counts as 1 length, not 8.
  const lengthModifierMap = { '&hellip;': 6 }
  const length = Math.max(0, (options?.length || 30) + (lengthModifierMap[omission] || omission?.length || 0))

  return _truncate(string, { length, omission, separator })
}

export function toStringArray(value) {
  if (typeof value === 'string') {
    return [value]
  }
  if (Array.isArray(value) && value.every((val) => typeof val === 'string')) {
    return value
  }

  return []
}

export function toNumber(value, fallback = 0) {
  if (typeof value === 'string') {
    return Number.parseInt(value)
  }

  if (typeof value === 'number') {
    return value
  }

  return fallback
}

/**
 * Returns difference in years or days (not cumulative) based on input dates (as string of Date objects)
 */
export function getDateDifference(a, b) {
  // Ensure both a and b are Date objects
  if (!(a instanceof Date)) a = a ? new Date(a) : new Date()
  if (!(b instanceof Date)) b = b ? new Date(b) : new Date()
  return {
    days: Math.floor(Math.abs((a.getTime() - b.getTime()) / (1000 * 60 * 60 * 24))),
    years: Math.abs(a.getFullYear() - b.getFullYear()),
  }
}

/**
 * Returns the segments from any given URL to determine the protocol, host and parameters
 * @param url
 * @returns {*}
 */
export function getUrlSegments(url) {
  return url.match(/^(?<protocol>[^:]+:)?(\/\/(?<host>[^/]+))?(?<rest>.*$)/i).groups
}

// From: nuxt/node_modules/@nuxt/utils/dist/utils.js
export function sortRoutes(routes) {
  routes.sort((a, b) => {
    if (!a.path.length) {
      return -1
    }
    if (!b.path.length) {
      return 1
    }
    // Order: /static, /index, /:dynamic
    // Match exact route before index: /login before /index/:slug
    if (a.path === '/') {
      return b.path.startsWith('/:') ? -1 : 1
    }
    if (b.path === '/') {
      return a.path.startsWith('/:') ? 1 : -1
    }

    let i
    let res = 0
    let y = 0
    let z = 0
    const a_ = a.path.split('/')
    const b_ = b.path.split('/')
    for (i = 0; i < a_.length; i++) {
      if (res !== 0) {
        break
      }
      y = [':slug', ':pathMatch'].includes(a_[i]) ? 2 : a_[i].includes(':') ? 1 : 0
      z = [':slug', ':pathMatch'].includes(b_[i]) ? 2 : b_[i].includes(':') ? 1 : 0
      res = y - z
      // If a.length >= b.length
      if (i === b_.length - 1 && res === 0) {
        // unless :slug/:pathMatch found sort by level, then alphabetically
        res = [':slug', ':pathMatch'].includes(a_[i]) ? -1 : a_.length === b_.length ? a.path.localeCompare(b.path) : a_.length - b_.length
      }
    }

    if (res === 0) {
      // unless :slug/:pathMatch found sort by level, then alphabetically
      res = [':slug', ':pathMatch'].includes(a_[i - 1]) && b_[i] ? 1 : a_.length === b_.length ? a.path.localeCompare(b.path) : a_.length - b_.length
    }
    return res
  })

  routes.forEach((route) => {
    if (route.children) {
      sortRoutes(route.children)
    }
  })

  return routes
}

export function createFullUrl(route: Route, config: ReturnType<typeof useRuntimeConfig>): string {
  const baseUrl =
    route.meta?.domain === 'ikZoekBaas'
      ? config.public['izbSiteUrl']
      : route.meta?.domain === 'mijnDierIsZoek'
        ? config.public['mdizSiteUrl']
        : route.meta?.domain === 'inspectiedienst'
          ? config.public['lidSiteUrl']
          : config.public['wwwSiteUrl']
  return new URL(route.fullPath, baseUrl).href
}

export function setPageCacheControlHeader(page: { expiryDate?: string | null }): void {
  const maxAge = calculateMaxAge(page.expiryDate)
  setCacheControlHeader(maxAge)
}

/** Make sure the max age is not 6 hours when the entry expires sooner. */
function calculateMaxAge(expiryDate?: string | null): number {
  if (!expiryDate) {
    return 21600
  }

  const now = Date.now()
  const expiry = new Date(expiryDate).getTime()
  const secondsUntilExpiry = Math.floor((expiry - now) / 1000)

  return secondsUntilExpiry <= 0 ? 0 : Math.min(secondsUntilExpiry, 21600)
}

export function setCacheControlHeader(seconds = 21600, mustRevalidate = false) {
  if (!import.meta.server) {
    return
  }

  const header = useResponseHeader('Cache-Control')
  header.value = `public, s-maxage=${seconds},${mustRevalidate ? ' must-revalidate,' : ''} stale-while-revalidate=${seconds}, stale-if-error=${seconds}`
}
